import React, { useMemo } from 'react';
import styled from 'styled-components';
import { ReactComponent as SyncIcon } from '@elementary/common-ui/assets/sync-arrows.svg';
import { ReactComponent as WebhookIcon } from '@elementary/common-ui/assets/webhook-icon.svg';
import {
  Chip,
  Flex,
  FlexCenter,
  FlexColumn,
  OverflowTooltip,
} from '@elementary/common-ui/components';
import { capitalize } from '@elementary/common-ui/utils/common';
import { localizeDate } from '@elementary/common-ui/utils/datetime';
import { getWarehouseIcon } from '@elementary/common-ui/utils/environments';
import { AccountEnvironment } from '@elementary/common-ui/types';
import { CircularProgress, Typography } from '@mui/material';
import cronParser from 'cron-parser';

const SyncIconStyled = styled(SyncIcon)`
  animation-direction: reverse;
  @keyframes rotateAnimation {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(359deg);
    }
  }
`;

const UpdatingLabel = styled(Typography)`
  font-size: 10px;
  color: ${({ theme }) => theme.palette.secondary.main};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  letter-spacing: 1px;
`;

const NameOverflowTooltip = styled(OverflowTooltip)`
  && {
    width: auto;
    max-width: 220px;
  }
`;

type EnvMenuLabelProps = {
  env: AccountEnvironment;
  isInitializing?: boolean;
};

const EnvMenuOptionLabel = ({ env, isInitializing }: EnvMenuLabelProps) => {
  const cronExpression = env?.cron;
  const nextAtLabel = useMemo(
    () =>
      cronExpression
        ? `Next ${localizeDate(
            cronParser.parseExpression(cronExpression).next().toDate(),
          ).fromNow()}`
        : 'Not scheduled',
    [cronExpression],
  );

  const updatedAtLabel = useMemo(
    () => `Updated ${localizeDate(env?.lastSyncedAt).fromNow()}`,
    [env],
  );

  return (
    <Flex sx={{ gap: 2, pr: 2 }}>
      <FlexCenter sx={{ svg: { width: '24px' } }}>
        {React.createElement(getWarehouseIcon(env.dwhConnection.warehouseType))}
      </FlexCenter>

      <FlexColumn sx={{ gap: '3px' }}>
        <Flex sx={{ alignItems: 'center', gap: 1.5 }}>
          <NameOverflowTooltip bold>{capitalize(env.name)}</NameOverflowTooltip>
          {env.environmentType === 'prod' && (
            <Chip
              label={capitalize(env.environmentType)}
              size="small"
              color="primary"
              bold
              overflow="visible"
            />
          )}
        </Flex>

        {!isInitializing ? (
          <FlexColumn sx={{ justifyContent: 'center', gap: '6px' }}>
            {env?.lastSyncedAt && (
              <Typography noWrap sx={{ fontSize: '12px' }}>
                {updatedAtLabel}
              </Typography>
            )}

            {env.isDeactivated ? (
              <Typography
                noWrap
                sx={{
                  fontSize: '12px',
                  color: 'common.red100',
                }}
              >
                Updates are deactivated
              </Typography>
            ) : (
              <Flex sx={{ alignItems: 'center', gap: '6px' }}>
                {env.lastSyncStatus !== 'running' &&
                  (env.webhookSetup ? <WebhookIcon /> : <SyncIconStyled />)}

                {env.lastSyncStatus === 'running' ? (
                  <UpdatingLabel noWrap>UPDATING...</UpdatingLabel>
                ) : (
                  <Typography
                    noWrap
                    sx={{
                      fontSize: '12px',
                      color: 'common.gray2',
                    }}
                  >
                    {env.webhookSetup ? 'Webhook triggered' : nextAtLabel}
                  </Typography>
                )}
              </Flex>
            )}
          </FlexColumn>
        ) : (
          <Flex sx={{ alignItems: 'center', gap: '6px', height: '100%' }}>
            <CircularProgress
              size={8}
              thickness={6}
              sx={{ color: 'common.green100' }}
            />
            <Typography
              noWrap
              sx={{
                fontSize: '12px',
                color: 'common.green100',
                fontWeight: 'bold',
              }}
            >
              Initializing...
            </Typography>
          </Flex>
        )}
      </FlexColumn>
    </Flex>
  );
};

export default EnvMenuOptionLabel;
